import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { getCompanies, type CompanyAccess } from '@/frontend/services/invitationService'
import { useAuth } from './AuthContext'

interface CompanyContextType {
  selectedCompany: CompanyAccess | null
  availableCompanies: CompanyAccess[]
  loading: boolean
  error: string | null
  selectCompany: (company: CompanyAccess) => void
  refreshCompanies: () => Promise<void>
  hasCompanyAccess: boolean
  isLoaded: boolean
}

const CompanyContext = createContext<CompanyContextType | null>(null)

interface CompanyProviderProps {
  children: ReactNode
}

const STORAGE_KEY = 'selected-company'

/**
 * Company Context Provider
 * Manages selected company state and available companies for the authenticated user
 */
export function CompanyProvider({ children }: CompanyProviderProps) {
  const { isSignedIn, user, loading: authLoading } = useAuth()
  const [selectedCompany, setSelectedCompany] = useState<CompanyAccess | null>(null)
  const [availableCompanies, setAvailableCompanies] = useState<CompanyAccess[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [companiesProcessed, setCompaniesProcessed] = useState(false)

  // Initial localStorage read on component mount
  useEffect(() => {
    console.log('[CompanyContext] Component mounted, attempting initial localStorage read')
    const storedCompany = localStorage.getItem(STORAGE_KEY)
    if (storedCompany) {
      try {
        const parsedCompany = JSON.parse(storedCompany) as CompanyAccess
        console.log('[CompanyContext] Found stored company on mount:', parsedCompany.company)
        // Set it immediately, will be validated later when companies load
        setSelectedCompany(parsedCompany)
      } catch (error) {
        console.error('[CompanyContext] Invalid stored company data on mount:', error)
        localStorage.removeItem(STORAGE_KEY)
      }
    } else {
      console.log('[CompanyContext] No stored company found on mount')
    }
  }, [])

  // Load companies when user signs in
  useEffect(() => {
    if (isSignedIn && user) {
      console.log('[CompanyContext] User signed in, loading companies')
      loadCompanies()
    } else if (!authLoading && !isSignedIn) {
      // Only clear data if auth is loaded and user is definitely signed out
      // This prevents clearing during initial auth loading phase
      console.log('[CompanyContext] User signed out (auth loaded), clearing company data')
      setSelectedCompany(null)
      setAvailableCompanies([])
      setIsLoaded(false)
      localStorage.removeItem(STORAGE_KEY)
    } else {
      console.log('[CompanyContext] Auth still loading, not clearing data yet')
    }
  }, [isSignedIn, user, authLoading])

  // Validate and restore company selection when companies are loaded
  useEffect(() => {
    if (availableCompanies.length > 0 && !companiesProcessed) {
      setCompaniesProcessed(true)
      if (selectedCompany) {
        // Validate the already selected company (from initial localStorage read)
        const isAvailable = availableCompanies.some(
          company => company.company === selectedCompany.company && company.module === selectedCompany.module
        )
        
        console.log('[CompanyContext] Validating pre-selected company:', {
          company: selectedCompany.company,
          isAvailable
        })
        
        if (!isAvailable) {
          console.warn('[CompanyContext] Pre-selected company no longer available, clearing selection')
          setSelectedCompany(null)
          localStorage.removeItem(STORAGE_KEY)
          
          // Auto-select if user has only one company
          if (availableCompanies.length === 1) {
            console.log('[CompanyContext] Auto-selecting single available company')
            selectCompany(availableCompanies[0])
          }
        } else {
          console.log('[CompanyContext] Pre-selected company is valid, keeping selection')
        }
      } else {
        // No pre-selected company, try to restore from localStorage or auto-select
        console.log('[CompanyContext] No pre-selected company, attempting restore or auto-select')
        
        const storedCompany = localStorage.getItem(STORAGE_KEY)
        if (storedCompany) {
          try {
            const parsedCompany = JSON.parse(storedCompany) as CompanyAccess
            const isAvailable = availableCompanies.some(
              company => company.company === parsedCompany.company && company.module === parsedCompany.module
            )
            
            if (isAvailable) {
              console.log('[CompanyContext] Restoring company from localStorage:', parsedCompany.company)
              setSelectedCompany(parsedCompany)
            } else {
              console.warn('[CompanyContext] Stored company no longer available')
              localStorage.removeItem(STORAGE_KEY)
            }
          } catch (error) {
            console.error('[CompanyContext] Invalid stored company data:', error)
            localStorage.removeItem(STORAGE_KEY)
          }
        }
        
        // Auto-select if user has only one company and nothing was restored
        if (availableCompanies.length === 1) {
          console.log('[CompanyContext] Auto-selecting single available company')
          selectCompany(availableCompanies[0])
        } else if (availableCompanies.length > 1) {
          console.log('[CompanyContext] Multiple companies available, user must choose')
        }
      }
    }
  }, [availableCompanies, companiesProcessed, selectedCompany])

  const loadCompanies = async () => {
    try {
      setLoading(true)
      setError(null)
      setCompaniesProcessed(false) // Reset flag when loading new companies
      
      console.log('[CompanyContext] Loading companies for user:', user?.uid)
      const response = await getCompanies()
      
      if (response.success && response.data) {
        // Filter only active companies
        const activeCompanies = response.data.filter(company => company.status === 'ACTIVE')
        setAvailableCompanies(activeCompanies)
        console.log('[CompanyContext] Loaded active companies:', {
          total: response.data.length,
          active: activeCompanies.length,
          companies: activeCompanies.map(c => ({ company: c.company, module: c.module, role: c.role }))
        })
      } else {
        throw new Error(response.error || 'Failed to load companies')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load companies'
      setError(errorMessage)
      console.error('[CompanyContext] Failed to load companies:', errorMessage)
    } finally {
      setLoading(false)
      setIsLoaded(true)
      console.log('[CompanyContext] Company loading completed, isLoaded set to true')
    }
  }

  const selectCompany = (company: CompanyAccess) => {
    console.log('[CompanyContext] Selecting company:', {
      company: company.company,
      module: company.module,
      role: company.role,
      storingToLocalStorage: true
    })
    setSelectedCompany(company)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(company))
    console.log('[CompanyContext] Company selection completed and stored')
  }

  const refreshCompanies = async () => {
    await loadCompanies()
  }

  const value: CompanyContextType = {
    selectedCompany,
    availableCompanies,
    loading,
    error,
    selectCompany,
    refreshCompanies,
    hasCompanyAccess: availableCompanies.length > 0,
    isLoaded,
  }

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  )
}

/**
 * Hook to access company context
 */
export function useCompany() {
  const context = useContext(CompanyContext)
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider')
  }
  return context
}

/**
 * Hook to get selected company info
 */
export function useSelectedCompany() {
  const { selectedCompany, loading, hasCompanyAccess } = useCompany()
  return {
    company: selectedCompany,
    isLoaded: !loading,
    hasAccess: hasCompanyAccess,
  }
}